import { GetServerSideProps } from 'next';
import React, { useMemo, useState } from 'react';

import {
  BlockCardWithBackground,
  DefaultPage,
  RenderBlocks,
} from '@klappir/container/graphcms';
import { getFilterConfigs, getPage, getPartners } from '@klappir/data/graphcms';
import { DataGraphcms } from '@klappir/types';
import { ArticleCard } from '@klappir/ui/card';
import { Column, Container, Row, SEO } from '@klappir/ui/core';
import { Filters } from '@klappir/ui/filters';
import styled from '@klappir/vendor/styled';

const ArticleContainer = styled.section`
  margin: 0 auto 64px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(336px, 1fr));
  grid-column-gap: 40px;
  grid-row-gap: 40px;
`;

const block = {
  _typename: 'BlockCardWithBackground',
  card: {
    title: { title: "Can't find what you are looking for?", coloredPart: '' },
    paragraph: '',
    button: { label: 'Contact Us', link: '/get-in-touch' },
    textAlign: 'center',
  },
};

const PartnersStories = ({
  content,
  partners,
  filterConfigs,
}: DataGraphcms.ArticlesPageProps) => {
  const [filters, setFilters] = useState<Record<string, string[]>>({
    countries: [],
    partnerType: [],
    specialization: [],
    search: [],
  });

  const dynamicFilterConfig = useMemo(
    () => [
      {
        id: 'search',
        label: 'Search',
        options: [],
        multiple: false,
      },
      {
        id: 'countries',
        label: 'Country',
        options: filterConfigs?.countries || [],
        multiple: true,
        sort: (a, b) => a.label.localeCompare(b.label),
      },
      {
        id: 'partnerType',
        label: 'Partner Type',
        options: filterConfigs?.partnerTypes || [],
        multiple: true,
        sort: (a, b) => a.label.localeCompare(b.label),
      },
      {
        id: 'specialization',
        label: 'Specialization',
        options: filterConfigs?.specializations || [],
        multiple: true,
        sort: (a, b) => a.label.localeCompare(b.label),
      },
    ],
    [filterConfigs]
  );

  const filteredPartners = useMemo(() => {
    const filtered = (partners || []).filter((partner) => {
      // First check the search term
      const searchTerm = filters.search[0]?.toLowerCase() || '';
      if (searchTerm && !partner.title?.toLowerCase().includes(searchTerm)) {
        return false;
      }

      // Then check other filters
      return Object.entries(filters).every(([key, selectedValues]) => {
        if (key === 'search') return true;
        if (!selectedValues.length) return true;

        const partnerValues = partner[key as keyof typeof partner];

        return (
          Array.isArray(partnerValues) &&
          selectedValues.every((selectedValue) =>
            partnerValues.some(
              (pValue) => pValue.toLowerCase() === selectedValue.toLowerCase()
            )
          )
        );
      });
    });

    // Sort the filtered partners alphabetically by title
    return filtered.sort((a, b) => a.title.localeCompare(b.title));
  }, [partners, filters]);

  const handleFilterChange = (filterId: string, value: string) => {
    setFilters((prev) => {
      if (filterId === 'search') {
        return {
          ...prev,
          [filterId]: value ? [value] : [],
        };
      }

      if (value === '') {
        return {
          ...prev,
          [filterId]: [],
        };
      }

      const currentValues = prev[filterId] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      return {
        ...prev,
        [filterId]: newValues,
      };
    });
  };

  if (!content) {
    return <div>Error 404?</div>;
  }

  return (
    <DefaultPage localizations={content.localizations}>
      {content?.seo && (
        <SEO canonical="https://www.klappir.com/articles" {...content.seo} />
      )}
      <RenderBlocks blocks={content?.blocks ?? []} />
      <Container>
        <Row>
          <Column tiny={12} md={12} big={10} huge={10}>
            <Filters
              filters={dynamicFilterConfig}
              values={filters}
              onChange={handleFilterChange}
            />
            <ArticleContainer>
              {filteredPartners.map((partner, i) => (
                <ArticleCard
                  key={i}
                  slug={partner.slug}
                  thumbnail={partner.thumbnail}
                  textLink="Connect"
                  type="partners"
                />
              ))}
            </ArticleContainer>
          </Column>
        </Row>
      </Container>
      <BlockCardWithBackground
        {...(block as DataGraphcms.BlockCardWithBackground)}
      />
    </DefaultPage>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const [content, partners, filterConfigs] = await Promise.all([
    getPage('partners-redesign', context.locale),
    getPartners(context.locale),
    getFilterConfigs(),
  ]);

  return {
    props: {
      content,
      partners,
      filterConfigs,
    },
  };
};

export default PartnersStories;
